/**
 * Copyright © 2026 Adnuntius AS.
 */
import {AdnListHelperService} from '../common/controller/list-helper.service';
import {Component, Inject, Input} from '@angular/core';
import * as _ from 'lodash';

import template from './order-list-page.component.html';
import {AdminListColumn, AdminResourceListPageBase} from '../common/controller/admin-resource-list-page-base';
import {getListChildren} from '../common/controller/resource-list-utils';
import {OBJECT_TYPE} from '../common/constants/object-type';
import {LineItemBulkActionService} from '../line-item/line-item-bulk-action.service';
import {LineItemResource} from '../../components/api/resources/resource/line-item.resource';
import {AdnTimeShifterService} from '../../components/util/adn-time-shifter.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';

@Component({
  standalone: false,
  selector: 'adn-order-list-page',
  template
})
export class OrderListPageComponent extends AdminResourceListPageBase {
  @Input() readOnly: boolean;

  readonly itemStateName = 'app.adv.orders.order';
  readonly advertiserItemStateName = 'app.adv.advertisers.advertiser';
  readonly teamItemStateName = 'app.adm.teams.team';
  readonly tierItemStateName = 'app.adm.tiers.tier';
  readonly userItemStateName = 'app.adm.users.user';
  readonly lineItemItemStateName = 'app.adv.line-items.line-item';

  readonly childColumns: AdminListColumn[] = [
    {param: 'name'},
    {param: 'userState'},
    {param: 'executionState'},
    {param: '', label: 'tier', sortable: false},
    {param: 'objectives', numeric: true},
    {param: 'startDate'},
    {param: 'endDate'},
    {param: 'updateTime'}
  ];

  constructor(
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    @Inject(AdnTimeShifterService) private readonly adnTimeShifter: any,
    private readonly adnListHelper: AdnListHelperService,
    @Inject(LineItemResource) private readonly LineItem: any,
    private readonly lineItemBulkActionService: LineItemBulkActionService
  ) {
    super(LocalNetworkProfile);
  }

  get visibleColumns(): AdminListColumn[] {
    const fields = this.list?.sel?.outputFields?.length
      ? this.list.sel.outputFields
      : (this.list?.defaultFields || []);
    return this.normalizeColumns(fields);
  }

  isFieldVisible(param: string): boolean {
    return this.isListFieldVisible(param);
  }

  filtersResolved(): boolean {
    const list = this.list;
    if (!list) {
      return true;
    }
    return !!(list.selectInQuery?.$resolved
      && list.joinQuery?.$resolved
      && list.dateQuery?.$resolved
      && list.targetQuery?.$resolved
      && list.teamQuery?.$resolved);
  }

  toggleChildren(item: any): void {
    this.list.getChildren?.(item);
  }

  childList(itemId: string): any {
    return this.list.children?.[itemId];
  }

  businessLinkDisabled(): boolean {
    return this.list?.perms?.MANAGE_BUSINESS !== true;
  }

  userLinkDisabled(): boolean {
    const perms = this.list?.perms || {};
    return perms.MANAGE_SYSTEM !== true
      && perms.AX_ADVERTISER_ADMIN !== true
      && perms.AX_ADVERTISER_TEAM_ADMIN !== true;
  }

  systemLinkDisabled(): boolean {
    return this.list?.perms?.MANAGE_SYSTEM !== true;
  }

  eCpmValue(item: any): any {
    const amount = _.get(item, ['spendDelivery', 'amount']);
    const currency = _.get(item, ['spendDelivery', 'currency']);
    const rendered = item?.renderedDelivery;
    if (!_.isFinite(amount) || !rendered) {
      return undefined;
    }
    return {
      amount: amount / rendered * 1000,
      currency
    };
  }

  cpcValue(item: any): any {
    const amount = _.get(item, ['spendDelivery', 'amount']);
    const currency = _.get(item, ['spendDelivery', 'currency']);
    const clicks = item?.clickDelivery;
    if (!_.isFinite(amount) || !clicks) {
      return undefined;
    }
    return {
      amount: amount / clicks,
      currency
    };
  }

  protected afterListInit(): void {
    if (!this.list) {
      return;
    }
    this.list.canExpand = true;
    if (!this.list.getChildren) {
      getListChildren(this.list, this.adnListHelper, OBJECT_TYPE.Order, this.LineItem);
    }
    if (!this.list.bulkAction) {
      this.list.bulkAction = this.lineItemBulkActionService.createBulkAction(this.list);
    }
  }
}
